// Lógica pura (sin React/Redux) para ordenar y filtrar la tabla de
// "Listado de inscritos" (Income.jsx/IncomeRow.jsx) por cualquier columna,
// incluyendo las columnas dinámicas (campos adicionales y productos
// adicionales) que varían según la configuración de cada evento. Se
// mantiene separada de los componentes para poder probarla aparte, y para
// que el criterio de orden/filtro de cada columna coincida exactamente con
// lo que IncomeRow.jsx ya muestra (mismas fórmulas, un solo lugar).
//
// Cada columna expone { valorOrden, valorFiltro }: valorOrden es el valor
// "crudo" comparable (número/epoch cuando aplica) para que ordenar sea
// correcto; valorFiltro es el texto tal como se ve en la celda, para que
// escribir en el filtro de una columna encuentre lo que el usuario
// realmente está viendo.

import {
  completeNull,
  modalidadCateogoriaNameByCode,
  timestampToDate,
  calcularEdad,
} from "../../../../../services/utils";
import { listarModalidades } from "../Configuration/BulkEdit/bulkEditLogic";
import {
  buscarCategoria,
  validarEdadEnCategoria,
  validarDorsalEnRango,
} from "../participantValidationLogic";

export const PREFIJO_CAMPO = "campo_";
export const PREFIJO_PRODUCTO = "producto_";

// Mismo diccionario que ya usa bulkEditLogic.js para traducir el código de
// rama guardado en el inscrito — se repite acá (en vez de importarlo) para
// no acoplar esta tabla a un archivo de otra funcionalidad por una
// constante de 3 valores.
const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };

// Mismo criterio que ya usa inventoryLogic.js para reconocer un campo de
// tipo "lista de selección": tiene sentido ofrecer sus opciones reales en
// el filtro de esa columna en vez de un campo de texto libre.
function esCampoSeleccionable(campo) {
  return (
    campo &&
    (campo.tipoCampo === "Select" || campo.tipoCampo === "option") &&
    Array.isArray(campo.valor)
  );
}

function opcionesDeCampo(campo) {
  return (campo.valor || []).map((v) => ({ value: v.valor, label: v.texto }));
}

// Opciones del filtro de "Modalidad": el valor mostrado en la celda es el
// nombre (no el código), así que las opciones del filtro también son el
// nombre — así seleccionar una opción siempre calza con lo que se ve.
export function opcionesModalidad(eventSubEventsRegistry) {
  const vistos = new Set();
  listarModalidades(eventSubEventsRegistry).forEach((m) => vistos.add(m.label));
  return Array.from(vistos)
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((nombre) => ({ value: nombre, label: nombre }));
}

// Opciones del filtro de "Categoria": se recorren TODAS las modalidades del
// evento (la columna no está ligada a una sola) y se juntan los nombres de
// categoría únicos, igual que arriba usando el nombre como valor.
export function opcionesCategoria(eventSubEventsRegistry) {
  const vistos = new Set();
  for (var modKey in eventSubEventsRegistry || {}) {
    const ramaCat = (eventSubEventsRegistry[modKey] || {}).ramaCat || {};
    for (var ramaKey in ramaCat) {
      const categorias = (ramaCat[ramaKey] || {}).categorias || {};
      for (var catKey in categorias) {
        const nombre = categorias[catKey].nombreCategoria;
        if (nombre) vistos.add(nombre);
      }
    }
  }
  return Array.from(vistos)
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((nombre) => ({ value: nombre, label: nombre }));
}

// Opciones del filtro de "Rama" (género): a diferencia de modalidad y
// categoría, lo que se guarda y se muestra en la celda es el código crudo
// (V/F/M) — el valor de la opción debe ser ese mismo código para que el
// filtro encuentre lo que realmente está escrito en la celda; la etiqueta
// sí se traduce para que el admin entienda qué está eligiendo.
export function opcionesRama(eventSubEventsRegistry) {
  const vistos = new Set();
  for (var modKey in eventSubEventsRegistry || {}) {
    const ramaCat = (eventSubEventsRegistry[modKey] || {}).ramaCat || {};
    for (var ramaKey in ramaCat) {
      vistos.add(ramaKey.replace("rama_", ""));
    }
  }
  return Array.from(vistos)
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((cod) => ({ value: cod, label: (NOMBRE_RAMA[cod] || cod) + " (" + cod + ")" }));
}

// Opciones del filtro de "Usuario": igual que resolverUsuario() más abajo,
// un inscrito queda con "on line" (se inscribió solo desde la web) o con el
// nombre del usuario autorizado que lo registró en punto de venta/importado.
export function opcionesUsuario(registryAutorizedUsers) {
  const vistos = new Set(["on line"]);
  for (var uid in registryAutorizedUsers || {}) {
    const nombre = registryAutorizedUsers[uid] && registryAutorizedUsers[uid].nombre;
    if (nombre) vistos.add(nombre);
  }
  return Array.from(vistos)
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((nombre) => ({ value: nombre, label: nombre }));
}

// Opciones del filtro de "Estatus": piezas atómicas que resolverEstatus()
// puede ir concatenando (un inscrito puede mostrar varias a la vez, ej.
// "Borrado Pagado en efectivo") — el filtro ya hace coincidencia por
// contención de texto, así que elegir una de estas también encuentra las
// filas donde aparece combinada con otra.
export const OPCIONES_ESTATUS = [
  "Borrado",
  "Registro onLine Incompleto",
  "Pendiente de pago en oxxo",
  "Pagado en oxxo",
  "Pagado en efectivo",
  "Pagado con tarjeta",
].map((texto) => ({ value: texto, label: texto }));

// Opciones del filtro de la columna "Alertas" — mismo criterio que ya usa
// IncomeRow.jsx (y ParticipantDetail) para marcar edad fuera del rango de
// la categoría o dorsal fuera del rango de la modalidad (ver
// participantValidationLogic.js).
//
// Ajuste temporal (2026-10): es una sola columna, pero el filtro permite
// escoger cuál de las dos alertas ver (edad o dorsal) — valorFiltro junta
// las que apliquen separadas por coma, y el filtro compara por
// "incluye" (ver coincideFiltros), así que elegir "Alerta edad" también
// encuentra a quien tiene las dos. Más adelante se revisa de nuevo este
// punto.
export const OPCIONES_WARNINGS = [
  { value: "Alerta edad", label: "Alerta edad" },
  { value: "Alerta dorsal", label: "Alerta dorsal" },
  { value: "Sin alerta", label: "Sin alerta" },
];

// Misma regla que ya usaba IncomeRow.jsx internamente (filterType) para el
// filtro de "Inscripciones completas / incompletas o borradas / todos".
export function filtraPorEstatus(inscritoData, filter) {
  if (
    filter === 1 &&
    inscritoData.statusPago &&
    inscritoData.statusPago.status === "ok" &&
    !inscritoData.borrado
  ) {
    return true;
  } else if (
    filter === 2 &&
    inscritoData.statusPago &&
    inscritoData.statusPago.status === "pendiente"
  ) {
    return true;
  } else if (filter === 3) {
    return true;
  } else if (filter === 4) {
    return true;
  }
  return false;
}

function resolverUsuario(inscritoData, registryAutorizedUsers) {
  if (
    inscritoData.statusPago.formaInscripcion === "punto de Venta" ||
    inscritoData.statusPago.formaInscripcion === "Importación masiva"
  ) {
    const usuarioAutorizado =
      registryAutorizedUsers &&
      registryAutorizedUsers[inscritoData.usuarioRegistra];
    return usuarioAutorizado ? usuarioAutorizado.nombre : "vacio";
  }
  return "on line";
}

function calcularPrecioCobrado(inscritoData) {
  var precioCobrado = 0;
  for (var p in inscritoData.order || {}) {
    precioCobrado =
      parseFloat(precioCobrado) +
      parseFloat(
        inscritoData.order[p].unit_price * inscritoData.order[p].quantity
      );
  }
  return precioCobrado;
}

function resolverEstatus(inscritoData) {
  var partes = [];
  if (inscritoData.borrado) partes.push("Borrado");
  if (inscritoData.statusPago.formaPago === "") {
    partes.push("Registro onLine Incompleto");
  }
  if (
    inscritoData.statusPago.formaPago === "oxxo_pay" &&
    inscritoData.statusPago.status === "pendiente"
  ) {
    partes.push("Pendiente de pago en oxxo");
  } else if (
    inscritoData.statusPago.formaPago === "oxxo_pay" &&
    inscritoData.statusPago.status === "ok"
  ) {
    partes.push("Pagado en oxxo");
  } else if (inscritoData.statusPago.formaPago === "efectivo") {
    partes.push("Pagado en efectivo");
  } else if (inscritoData.statusPago.formaPago === "card") {
    partes.push("Pagado con tarjeta");
  }
  return partes.join(" ");
}

// Mismas dos validaciones que ya se muestran en ParticipantDetail (edad vs.
// rango de la categoría, dorsal vs. rango de folios de la modalidad) — acá
// solo para poder filtrar/ordenar las columnas "Alerta edad"/"Alerta
// dorsal" de la tabla. Separadas (en vez de una sola función combinada)
// porque ahora son dos columnas/filtros independientes.
function tieneAlertaEdad(inscritoData, contexto) {
  const edadActual =
    contexto.registryConfiguration &&
    contexto.registryConfiguration.configuracion &&
    contexto.registryConfiguration.configuracion.edadActual;
  const edad = calcularEdad(
    inscritoData.fechaNacimiento,
    contexto.eventDetail && contexto.eventDetail.fechaEvento,
    edadActual
  );
  const categoriaObj = buscarCategoria(
    contexto.eventSubEventsRegistry,
    inscritoData.modalidad,
    inscritoData.rama,
    inscritoData.categoria
  );
  return !validarEdadEnCategoria(edad, categoriaObj).valido;
}

function tieneAlertaDorsal(inscritoData, contexto) {
  // Si casi todos los dorsales de esta modalidad están fuera de rango, es
  // señal de que el cliente cambió el rango de folios de la distancia (no
  // errores individuales) — se omite la alerta para toda la modalidad en
  // el listado (ver calcularModalidadesConRangoDorsalCambiado).
  if (
    contexto.modalidadesConRangoDorsalCambiado &&
    contexto.modalidadesConRangoDorsalCambiado[inscritoData.modalidad]
  ) {
    return false;
  }
  const configModalidad =
    contexto.eventSubEventsRegistry &&
    contexto.eventSubEventsRegistry["modalidad_" + inscritoData.modalidad] &&
    contexto.eventSubEventsRegistry["modalidad_" + inscritoData.modalidad].configModalidad;
  return !validarDorsalEnRango(
    inscritoData.numero,
    configModalidad && configModalidad.folios
  ).valido;
}

// Misma búsqueda que ya hace IncomeRow.jsx por cada producto adicional:
// localiza, si existe, la línea de "order" correspondiente a ese producto.
function buscarLineaProducto(inscritoData, codigoProducto) {
  var orden = inscritoData.order;
  if (!orden) return null;
  var codeProducto =
    codigoProducto === "códigoDescuento"
      ? "códigoDescuento"
      : "producto-" + codigoProducto;
  for (var g in orden) {
    if (orden[g] && orden[g].code === codeProducto) {
      return orden[g];
    }
  }
  return null;
}

function valorCampoAdicional(inscritoData, camposAdicionalesArray, iDCampo) {
  const valorCrudo = inscritoData[iDCampo];
  if (valorCrudo === undefined) {
    return { valorOrden: "", valorFiltro: "N/A" };
  }
  return { valorOrden: valorCrudo, valorFiltro: completeNull(valorCrudo) };
}

function valorProducto(inscritoData, productosAdicionalesArray, columnId) {
  const resto = columnId.slice(PREFIJO_PRODUCTO.length);
  const producto = productosAdicionalesArray.find(
    (p) => resto === p.codigoProducto + "_cantidad" ||
      resto === p.codigoProducto + "_precio" ||
      resto === p.codigoProducto + "_total" ||
      resto.indexOf(p.codigoProducto + "_campo_") === 0
  );
  if (!producto) return { valorOrden: "", valorFiltro: "" };

  const linea = buscarLineaProducto(inscritoData, producto.codigoProducto);

  if (resto === producto.codigoProducto + "_cantidad") {
    const v = linea ? linea.quantity : 0;
    return { valorOrden: v, valorFiltro: String(v) };
  }
  if (resto === producto.codigoProducto + "_precio") {
    const v = linea ? linea.unit_price : producto.precioProducto;
    return { valorOrden: v, valorFiltro: String(v) };
  }
  if (resto === producto.codigoProducto + "_total") {
    const v = linea ? linea.quantity * linea.unit_price : 0;
    return { valorOrden: v, valorFiltro: String(v) };
  }
  // columna de un campo propio del producto (ej. talla elegida)
  const iDCampo = resto.slice((producto.codigoProducto + "_campo_").length);
  const valorExtra =
    linea && linea.extraData ? linea.extraData[iDCampo] : undefined;
  if (valorExtra === undefined) {
    return { valorOrden: "", valorFiltro: linea ? "N/R" : "N/A" };
  }
  return { valorOrden: valorExtra, valorFiltro: String(valorExtra) };
}

// contexto = { eventSubEventsRegistry, registryAutorizedUsers,
//   apellidosSeparados, camposAdicionalesArray, productosAdicionalesArray }
export function obtenerValorColumna(inscritoData, columnId, contexto) {
  switch (columnId) {
    case "numero":
      return {
        valorOrden: Number(inscritoData.numero) || 0,
        valorFiltro: completeNull(inscritoData.numero),
      };
    case "nombres":
      return {
        valorOrden: inscritoData.nombres || "",
        valorFiltro: completeNull(inscritoData.nombres),
      };
    case "apellidoPaterno":
      return {
        valorOrden: inscritoData.apellidoPaterno || "",
        valorFiltro: completeNull(inscritoData.apellidoPaterno),
      };
    case "apellidoMaterno":
      return {
        valorOrden: inscritoData.apellidoMaterno || "",
        valorFiltro: completeNull(inscritoData.apellidoMaterno),
      };
    case "apellidos":
      return {
        valorOrden: inscritoData.apellidos || "",
        valorFiltro: completeNull(inscritoData.apellidos),
      };
    case "correo":
      return {
        valorOrden: inscritoData.correo || "",
        valorFiltro: completeNull(inscritoData.correo),
      };
    case "telefono":
      return {
        valorOrden: inscritoData.telefono || "",
        valorFiltro: completeNull(inscritoData.telefono),
      };
    case "modalidad": {
      const modCat = modalidadCateogoriaNameByCode(
        contexto.eventSubEventsRegistry,
        inscritoData.modalidad,
        inscritoData.categoria,
        inscritoData.rama
      );
      return {
        valorOrden: modCat.modalidad || "",
        valorFiltro: completeNull(modCat.modalidad),
      };
    }
    case "rama":
      return {
        valorOrden: inscritoData.rama || "",
        valorFiltro: completeNull(inscritoData.rama),
      };
    case "categoria": {
      const modCat = modalidadCateogoriaNameByCode(
        contexto.eventSubEventsRegistry,
        inscritoData.modalidad,
        inscritoData.categoria,
        inscritoData.rama
      );
      return {
        valorOrden: modCat.categoria || "",
        valorFiltro: completeNull(modCat.categoria),
      };
    }
    case "usuario": {
      const usuario = resolverUsuario(
        inscritoData,
        contexto.registryAutorizedUsers
      );
      return { valorOrden: usuario, valorFiltro: usuario };
    }
    case "precioCobrado": {
      const precio = calcularPrecioCobrado(inscritoData);
      return { valorOrden: precio, valorFiltro: String(precio) };
    }
    case "warnings": {
      const alertaEdad = tieneAlertaEdad(inscritoData, contexto);
      const alertaDorsal = tieneAlertaDorsal(inscritoData, contexto);
      const etiquetas = [];
      if (alertaEdad) etiquetas.push("Alerta edad");
      if (alertaDorsal) etiquetas.push("Alerta dorsal");
      return {
        valorOrden: etiquetas.length,
        valorFiltro: etiquetas.length ? etiquetas.join(", ") : "Sin alerta",
      };
    }
    case "fechaRegistro": {
      const ts = inscritoData.serverTime;
      if (ts !== undefined) {
        return { valorOrden: ts, valorFiltro: timestampToDate(ts) };
      }
      // Fallback para registros viejos sin serverTime: fechaRegistro es un
      // string ("2026/10/02 12:51:15") — se parsea a epoch también, para
      // que el filtro de rango desde/hasta funcione igual en ambos casos.
      if (inscritoData.fechaRegistro) {
        const parsed = new Date(inscritoData.fechaRegistro).getTime();
        return {
          valorOrden: isNaN(parsed) ? "" : parsed,
          valorFiltro: inscritoData.fechaRegistro,
        };
      }
      return { valorOrden: "", valorFiltro: completeNull(inscritoData.fechaRegistro) };
    }
    case "estatus": {
      const estatus = resolverEstatus(inscritoData);
      return { valorOrden: estatus, valorFiltro: estatus };
    }
    default:
      if (columnId.indexOf(PREFIJO_CAMPO) === 0) {
        return valorCampoAdicional(
          inscritoData,
          contexto.camposAdicionalesArray,
          columnId.slice(PREFIJO_CAMPO.length)
        );
      }
      if (columnId.indexOf(PREFIJO_PRODUCTO) === 0) {
        return valorProducto(
          inscritoData,
          contexto.productosAdicionalesArray,
          columnId
        );
      }
      return { valorOrden: "", valorFiltro: "" };
  }
}

// Comparador numérico-consciente: si ambos valores son números (o lo
// parecen), compara numéricamente; si no, compara como texto
// insensible a mayúsculas/acentos de orden simple.
export function compararValores(a, b) {
  const numA = typeof a === "number" ? a : parseFloat(a);
  const numB = typeof b === "number" ? b : parseFloat(b);
  const ambosNumericos =
    a !== "" && b !== "" && !isNaN(numA) && !isNaN(numB);
  if (ambosNumericos) {
    return numA - numB;
  }
  return String(a).localeCompare(String(b), "es", { sensitivity: "base" });
}

export function ordenarInscritos(entradas, columnId, direccion, contexto) {
  if (!columnId) return entradas;
  const factor = direccion === "desc" ? -1 : 1;
  return [...entradas].sort(([, dataA], [, dataB]) => {
    const { valorOrden: a } = obtenerValorColumna(dataA, columnId, contexto);
    const { valorOrden: b } = obtenerValorColumna(dataB, columnId, contexto);
    return factor * compararValores(a, b);
  });
}

// filtros: { columnId: textoEscritoPorElUsuario | {desde, hasta} }. Una fila
// debe coincidir con TODOS los filtros activos (AND). Los filtros de texto
// (incluidos los "select") hacen contención sin importar mayúsculas; los de
// rango (ej. fecha de registro) comparan el valor numérico/epoch contra
// desde/hasta, ambos opcionales.
export function coincideFiltros(inscritoData, filtros, contexto) {
  for (var columnId in filtros) {
    const filtro = filtros[columnId];
    if (!filtro) continue;

    if (typeof filtro === "object") {
      if (filtro.desde == null && filtro.hasta == null) continue;
      const { valorOrden } = obtenerValorColumna(inscritoData, columnId, contexto);
      const valorNumerico = typeof valorOrden === "number" ? valorOrden : parseFloat(valorOrden);
      if (isNaN(valorNumerico)) return false;
      if (filtro.desde != null && valorNumerico < filtro.desde) return false;
      if (filtro.hasta != null && valorNumerico > filtro.hasta) return false;
      continue;
    }

    const texto = String(filtro).trim().toLowerCase();
    if (!texto) continue;
    const { valorFiltro } = obtenerValorColumna(
      inscritoData,
      columnId,
      contexto
    );
    if (!String(valorFiltro).toLowerCase().includes(texto)) {
      return false;
    }
  }
  return true;
}

// Arma la lista {id, label} de columnas dinámicas en el mismo orden en que
// Income.jsx ya las arma para los encabezados — a partir de los mismos
// arreglos (camposAdicionalesModalidad / productosAdicionalesModalidadTitulo)
// que ya recibe IncomeRow.jsx, para que no se puedan desalinear.
export function construirColumnasDinamicas(
  camposAdicionalesArray,
  productosAdicionalesArray
) {
  const columnas = [];

  (camposAdicionalesArray || []).forEach((campo) => {
    const columna = {
      id: PREFIJO_CAMPO + campo.iDCampo,
      label: campo.etiqueta,
    };
    if (esCampoSeleccionable(campo)) {
      columna.tipo = "select";
      columna.opciones = opcionesDeCampo(campo);
    }
    columnas.push(columna);
  });

  (productosAdicionalesArray || []).forEach((producto) => {
    const base = PREFIJO_PRODUCTO + producto.codigoProducto;
    columnas.push({ id: base + "_cantidad", label: producto.nombreProducto + " - Cantidad" });
    columnas.push({ id: base + "_precio", label: producto.nombreProducto + " - Precio" });
    columnas.push({ id: base + "_total", label: producto.nombreProducto + " - Total" });
    // formularioProducto es un objeto keyed (no un array) — mismo caso ya
    // visto en OrderDetail.jsx/orderDetailLogic.js.
    Object.values(producto.formularioProducto || {}).forEach((campo) => {
      const columnaCampo = {
        id: base + "_campo_" + campo.iDCampo,
        label: campo.etiqueta,
      };
      if (esCampoSeleccionable(campo)) {
        columnaCampo.tipo = "select";
        columnaCampo.opciones = opcionesDeCampo(campo);
      }
      columnas.push(columnaCampo);
    });
  });

  return columnas;
}
